// client/src/components/UserStatsDetail.js
// Detailed per-user statistics page for admins and superadmins

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AnalyticsReportingService from '../services/analyticsReportingService';
import { getAdminUrl } from '../utils/urlUtils';
import { isAdmin, isSuperAdmin } from '../utils/roleUtils';
import { validateUserAction } from '../utils/orgDataUtils';
import { HeadlineMedium, HeadlineSmall, LabelMedium, BodyMedium } from './Typography';

/**
 * Format an ISO date string for display in tables.
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
const formatDateTime = (isoString) => {
  if (!isoString) return '—';

  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (err) {
    return '—';
  }
};

/**
 * Format session duration in seconds to a readable string.
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Summary card for a resource type's unique/total metrics.
 * @param {{ title: string, metrics: Object, showCompleted?: boolean }} props
 */
const ResourceSummaryCard = ({ title, metrics, showCompleted = true }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <HeadlineSmall color="gray-900" className="mb-4">{title}</HeadlineSmall>
    <div className="space-y-2">
      <BodyMedium color="gray-700">
        Accessed: <span className="font-semibold text-gray-900">{metrics?.accessedUnique ?? 0} unique</span>
        {' / '}
        <span className="font-semibold text-gray-900">{metrics?.accessedTotal ?? 0} visits</span>
      </BodyMedium>
      {showCompleted && (
        <BodyMedium color="gray-700">
          Completed: <span className="font-semibold text-gray-900">{metrics?.completedUnique ?? 0}</span>
        </BodyMedium>
      )}
    </div>
  </div>
);

/**
 * Resource breakdown tables for accessed and completed items.
 * @param {{ title: string, resources: { accessed: Array, completed: Array } }} props
 */
const ResourceSection = ({ title, resources }) => (
  <div className="bg-white rounded-lg shadow p-6 space-y-6">
    <HeadlineSmall color="gray-900">{title}</HeadlineSmall>

    <div>
      <LabelMedium color="gray-700" className="block mb-3">Accessed</LabelMedium>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Title</LabelMedium></th>
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">First accessed</LabelMedium></th>
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Last accessed</LabelMedium></th>
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Visits</LabelMedium></th>
            </tr>
          </thead>
          <tbody>
            {(resources?.accessed || []).map((item) => (
              <tr key={item.resourceId} className="border-b border-gray-100">
                <td className="py-2 px-3"><BodyMedium color="gray-900">{item.title}</BodyMedium></td>
                <td className="py-2 px-3"><BodyMedium color="gray-900">{formatDateTime(item.firstAccessed)}</BodyMedium></td>
                <td className="py-2 px-3"><BodyMedium color="gray-900">{formatDateTime(item.lastAccessed)}</BodyMedium></td>
                <td className="py-2 px-3"><BodyMedium color="gray-900">{item.visitCount}</BodyMedium></td>
              </tr>
            ))}
            {(resources?.accessed || []).length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-center">
                  <BodyMedium color="gray-500">No access records</BodyMedium>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    <div>
      <LabelMedium color="gray-700" className="block mb-3">Completed</LabelMedium>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Title</LabelMedium></th>
              <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Completed</LabelMedium></th>
            </tr>
          </thead>
          <tbody>
            {(resources?.completed || []).map((item) => (
              <tr key={item.resourceId} className="border-b border-gray-100">
                <td className="py-2 px-3"><BodyMedium color="gray-900">{item.title}</BodyMedium></td>
                <td className="py-2 px-3"><BodyMedium color="gray-900">{formatDateTime(item.completedAt)}</BodyMedium></td>
              </tr>
            ))}
            {(resources?.completed || []).length === 0 && (
              <tr>
                <td colSpan={2} className="py-4 text-center">
                  <BodyMedium color="gray-500">No completion records</BodyMedium>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const UserStatsDetail = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('org') || '';

  const { userRole, userOrganizations } = useAuth();

  const [stats, setStats] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedRange, setAppliedRange] = useState({ startDate: '', endDate: '' });

  /** Verify the caller can view this user's stats in the selected organization. */
  const verifyAccess = useCallback(async () => {
    if (!isAdmin(userRole)) {
      setAccessDenied(true);
      return false;
    }

    if (!organizationId || !userId) {
      setError('Organization and user are required.');
      return false;
    }

    if (!isSuperAdmin(userRole) && !(userOrganizations || []).includes(organizationId)) {
      setAccessDenied(true);
      return false;
    }

    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) {
      setError('User not found.');
      return false;
    }

    const targetUser = { id: userId, ...userSnap.data() };
    if (!(targetUser.organizationIds || []).includes(organizationId)) {
      setError('This user does not belong to the selected organization.');
      return false;
    }

    if (!validateUserAction(userRole, userOrganizations || [], targetUser, 'view')) {
      setAccessDenied(true);
      return false;
    }

    return true;
  }, [userRole, userOrganizations, organizationId, userId]);

  /** Load organization display name from Firestore. */
  const loadOrganizationName = useCallback(async () => {
    if (!organizationId) return;

    try {
      const orgSnap = await getDoc(doc(db, 'organizations', organizationId));
      if (orgSnap.exists()) {
        const data = orgSnap.data();
        setOrganizationName(data.name || data.organizationName || data.title || organizationId);
      } else {
        setOrganizationName(organizationId);
      }
    } catch (err) {
      setOrganizationName(organizationId);
    }
  }, [organizationId]);

  /** Fetch detailed user statistics from the analytics API. */
  const loadStats = useCallback(async (range = appliedRange) => {
    if (!organizationId || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const allowed = await verifyAccess();
      if (!allowed) {
        setLoading(false);
        return;
      }

      const detail = await AnalyticsReportingService.getUserStatsDetail(
        organizationId,
        userId,
        {
          startDate: range.startDate || undefined,
          endDate: range.endDate || undefined
        }
      );

      setStats(detail);
    } catch (err) {
      console.error('Error loading user statistics:', err);
      setError(err.message || 'Failed to load user statistics.');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId, verifyAccess, appliedRange]);

  useEffect(() => {
    loadOrganizationName();
  }, [loadOrganizationName]);

  useEffect(() => {
    loadStats();
  }, [organizationId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyDateRange = () => {
    const nextRange = { startDate, endDate };
    setAppliedRange(nextRange);
    loadStats(nextRange);
  };

  const handleClearDateRange = () => {
    setStartDate('');
    setEndDate('');
    const clearedRange = { startDate: '', endDate: '' };
    setAppliedRange(clearedRange);
    loadStats(clearedRange);
  };

  if (accessDenied) {
    return (
      <div className="text-center py-12">
        <BodyMedium color="red-600">Access denied. Admin privileges required.</BodyMedium>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="space-y-4">
        <Link
          to={getAdminUrl('students')}
          className="inline-flex items-center min-h-[44px] text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
        >
          ← Back to Students
        </Link>
        <BodyMedium color="gray-700">Missing organization context. Open this page from the Students list.</BodyMedium>
      </div>
    );
  }

  const displayName = stats?.displayName || stats?.email || userId;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={getAdminUrl('students')}
          className="inline-flex items-center min-h-[44px] text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded mb-4"
        >
          ← Back to Students
        </Link>
        <HeadlineMedium color="gray-900">{displayName}</HeadlineMedium>
        <BodyMedium color="gray-600" className="mt-1">
          {stats?.email || 'Loading user…'} · {organizationName}
        </BodyMedium>
      </div>

      {/* Date range filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <LabelMedium color="gray-700" className="block mb-3">Filter by date range</LabelMedium>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="stats-start-date" className="block text-sm text-gray-600 mb-1">Start date</label>
            <input
              id="stats-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="stats-end-date" className="block text-sm text-gray-600 mb-1">End date</label>
            <input
              id="stats-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-[44px]"
            />
          </div>
          <button
            type="button"
            onClick={handleApplyDateRange}
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={handleClearDateRange}
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden />
          <span className="ml-3 text-gray-600">Loading statistics…</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4" role="alert">
          <BodyMedium color="red-800">{error}</BodyMedium>
        </div>
      ) : stats ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <HeadlineSmall color="gray-900" className="mb-4">Logins</HeadlineSmall>
              <BodyMedium color="gray-700">
                Total: <span className="font-semibold text-gray-900">{stats.logins ?? 0}</span>
              </BodyMedium>
              <BodyMedium color="gray-700" className="mt-2">
                Last login: <span className="font-semibold text-gray-900">{formatDateTime(stats.lastLogin)}</span>
              </BodyMedium>
            </div>
            <ResourceSummaryCard title="Courses" metrics={stats.courses} />
            <ResourceSummaryCard title="Lessons" metrics={stats.lessons} />
            <ResourceSummaryCard title="Prompts" metrics={stats.prompts} />
          </div>

          {/* Login history */}
          <div className="bg-white rounded-lg shadow p-6">
            <HeadlineSmall color="gray-900" className="mb-4">Login history</HeadlineSmall>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Login time</LabelMedium></th>
                    <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Duration</LabelMedium></th>
                    <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Auto logout</LabelMedium></th>
                    <th scope="col" className="text-left py-2 px-3"><LabelMedium color="gray-600">Active</LabelMedium></th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.loginHistory || []).map((session) => (
                    <tr key={session.sessionId} className="border-b border-gray-100">
                      <td className="py-2 px-3"><BodyMedium color="gray-900">{formatDateTime(session.loginTime)}</BodyMedium></td>
                      <td className="py-2 px-3"><BodyMedium color="gray-900">{formatDuration(session.duration)}</BodyMedium></td>
                      <td className="py-2 px-3"><BodyMedium color="gray-900">{session.autoLogout ? 'Yes' : 'No'}</BodyMedium></td>
                      <td className="py-2 px-3"><BodyMedium color="gray-900">{session.isActive ? 'Yes' : 'No'}</BodyMedium></td>
                    </tr>
                  ))}
                  {(stats.loginHistory || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center">
                        <BodyMedium color="gray-500">No login sessions recorded</BodyMedium>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Resource breakdowns */}
          <ResourceSection title="Courses" resources={stats.resources?.courses} />
          <ResourceSection title="Lessons" resources={stats.resources?.lessons} />
          <ResourceSection title="Prompts" resources={stats.resources?.prompts} />
        </>
      ) : null}
    </div>
  );
};

export default UserStatsDetail;
